import { enablePointerEvents } from '@utils/pointer-events-control';
import { config, gameState } from '@js/state';
import {
  TUTORIAL_ROUND,
  generateRounds,
  PUMP_ORDER,
  PUMP_COLOR,
  PUMP_COLOR_CSS,
  LIVES_PER_ROUND,
  WIN_DOTS,
  gcd,
} from '@utils/roundGenerator';
import { SPACE_BG, addLaunchpad, roundBgKey } from '@utils/space';

// ─── Exact Figma geometry (frame-relative, from absoluteBoundingBox) ───
const PUMP = { y: 370, xs: [233, 446, 659] };
const WINDOW_DY = -38;
const ROCKET = { x: 993, y: 309 };
// Launch-animation sprite placement. The atlas is full-frame with the rocket
// centred (~640); offset x so the launch rises from the parked rocket on the right.
const ROCKET_ANIM = { x: 993, y: 330 };
const KEYPAD = { x: 993, y: 578, hiddenY: 905 };
const PIPE_C = { orange: [630, 450], blue: [736, 450], purple: [843, 450] };
const PUMP_ANIM_PREFIX = { orange: 'OPump', blue: 'BPump', purple: 'PPump' };

// Percentage bar (640,107) 424x53 — Figma 536:1127 default look: SHARP corners,
// dark #313131 segments, #dadada dividers/border, % text in the pump colours.
// A segment fills with its pump colour as that value is answered.
const BAR = { cx: 640, cy: 107, w: 424, h: 53, border: 2 };
const C_SEG_EMPTY = 0x313131; // Z-Black segment (536:1127)
const C_DIVIDER = 0xdadada;
const WINDOW_TXT = '#313131'; // pump-window digit (dark on white window)
const C_CORRECT = '#007038'; // Green 6
const C_WRONG = 0xec5b13; // Orange 5

// Score panel green dots — panel at (1229,165); dots align to the grey lamps.
const SCORE_DOT = { x: 1210, y0: 61, dy: 42 };

// Keypad keys — key-face centres measured from keypad.png pixels (agrees with
// the Figma keypad group 536:40535 to <1px). Footprint 48×57.6 incl. the
// 1.8px border; Clear is 105.6 wide.
const KEY_LAYOUT = [
  { label: 1, dx: -58.5, dy: -103.5 }, { label: 2, dx: -0.5, dy: -103.5 }, { label: 3, dx: 57.5, dy: -103.5 },
  { label: 4, dx: -58.5, dy: -36 }, { label: 5, dx: -0.5, dy: -36 }, { label: 6, dx: 57.5, dy: -36 },
  { label: 7, dx: -58.5, dy: 31 }, { label: 8, dx: -0.5, dy: 31 }, { label: 9, dx: 57.5, dy: 31 },
  { label: 0, dx: -58.5, dy: 98.5 }, { label: 'clear', dx: 28.5, dy: 98.5, wide: true },
];
const KEY_W = 48;
const KEY_H = 57.6;
const KEY_WIDE_W = 105.6;

// Hint system (pizza-game convention — button Figma 1742:4856, card 1742:5104):
// the lightbulb pops in at bottom-left on a wrong attempt in a scored round;
// clicking it opens a dim-overlay modal with the divide-by-GCD hint.
const HINT = { x: 88, y: 632, size: 80 }; // PNG is a 2x export — sized via setDisplaySize
const HINT_CARD = { x: 72, y: 422, w: 440, h: 234, r: 16 };
const HINT_CROSS = { cx: 532, cy: 422, s: 32 };

export default class GameScene extends Phaser.Scene {
  constructor() {
    super({ key: 'GameScene' });
  }

  create() {
    // Enable pointer events so hover states and the cursor work
    enablePointerEvents();

    // Round 0 = guided tutorial; rounds 1..9 = procedurally generated + scored.
    this.rounds = [TUTORIAL_ROUND, ...generateRounds()];
    this.roundIndex = 0;

    this.cameras.main.setBackgroundColor(SPACE_BG);
    this.cameras.main.fadeIn(350, 0, 0, 0);

    this.bgPlate = this.add.image(640, 360, roundBgKey(0)).setDepth(0); // per-round scenery plate
    addLaunchpad(this); // launchpad platform on top
    this.add.image(1229, 165, 'traffic-light').setDepth(2.5); // score panel body

    // Green score dots (one per earned round), hidden until lit.
    this.scoreDots = [];
    for (let i = 0; i < WIN_DOTS; i++) {
      this.scoreDots.push(
        this.add.image(SCORE_DOT.x, SCORE_DOT.y0 + i * SCORE_DOT.dy, 'score-dot-green')
          .setDepth(2.6).setVisible(false),
      );
    }

    this.pipes = {};
    PUMP_ORDER.forEach((color, i) => {
      this.pipes[color] = this.add
        .sprite(640, 360, 'pumpAnims', `${PUMP_ANIM_PREFIX[color]}00.png`)
        .setOrigin(0.5)
        .setDepth(4.5 + (PUMP_ORDER.length - 1 - i) * 0.01);
    });

    this.pumps = [];
    this.windowTexts = [];
    PUMP_ORDER.forEach((color, i) => {
      const px = PUMP.xs[i];
      this.pumps.push(this.add.image(px, PUMP.y, `pump-${color}`).setDepth(4));
      this.windowTexts.push(
        this.add.text(px, PUMP.y + WINDOW_DY, '', {
          fontFamily: 'Athletics-Bold', fontSize: '30px', color: WINDOW_TXT,
        }).setOrigin(0.5).setDepth(5),
      );
    });

    this.rocketHome = { x: ROCKET.x, y: ROCKET.y };
    this.rocket = this.add
      .image(ROCKET.x, ROCKET.y, 'rocket')
      .setOrigin(0.5)
      .setDepth(5);

    this.barC = this.add.container(0, 0).setDepth(6);
    this.barG = this.add.graphics();
    this.barC.add(this.barG);
    this.barFills = [];
    this.barTexts = [];

    this.buildKeypad();
    this.hand = this.add.image(0, 0, 'hand').setOrigin(0.1, 0.1).setDepth(20).setVisible(false);

    this.hintModal = null;
    this.hintBtn = this.buildHintButton();

    this.inputEnabled = false;
    this.keypadUp = false;
    this.launching = false;
    this.input = '';

    if (!this.anims.exists('rocketLaunch')) {
      this.anims.create({
        key: 'rocketLaunch',
        frames: this.anims.generateFrameNames('rocketAnims', {
          prefix: 'rocket landing', start: 0, end: 59, suffix: '.png', zeroPad: 2,
        }),
        frameRate: 30,
        repeat: 0,
      });
    }

    PUMP_ORDER.forEach(color => {
      const key = `pumpAnim-${color}`;
      if (!this.anims.exists(key)) {
        this.anims.create({
          key,
          frames: this.anims.generateFrameNames('pumpAnims', {
            prefix: PUMP_ANIM_PREFIX[color], start: 0, end: 39, suffix: '.png', zeroPad: 2,
          }),
          frameRate: 30,
          repeat: 0,
        });
      }
    });

    this.startRound(0);
  }

  // ════════════════════════ ROUND LIFECYCLE ════════════════════════

  startRound(idx) {
    this.roundIndex = idx;
    this.round = this.rounds[idx];
    this.guided = idx === 0;
    this.currentPump = 0;
    this.lives = LIVES_PER_ROUND;
    this.input = '';
    this.hideHintButton();

    this.bgPlate.setTexture(roundBgKey(idx)); // swap to this round's background
    this.windowTexts.forEach(t => t.setText('').setColor(WINDOW_TXT));
    PUMP_ORDER.forEach(c => this.resetPipe(c));
    this.buildBar(this.round);

    if (idx > 0) {
      // New level (arrived via warp): the rocket lands before the pumps
      // activate — the atlas frames are authored as "rocket landing", so
      // forward playback descends onto the pad.
      this.rocket.setVisible(false);
      this.landRocket(() => this.time.delayedCall(450, () => this.activatePump(0)));
    } else {
      this.parkRocket();
      this.time.delayedCall(450, () => this.activatePump(0));
    }
  }

  parkRocket() {
    this.rocket.setPosition(this.rocketHome.x, this.rocketHome.y).setAngle(0).setAlpha(1).setVisible(true);
  }

  activatePump(p) {
    this.currentPump = p;
    this.input = '';
    this.updateWindow();
    this.pulsePump(p);
    // The pipe connects only after a correct answer (see evaluate -> connectPipe).
    if (!this.keypadUp) {
      this.slideKeypad(KEYPAD.y, () => { this.keypadUp = true; this.enableInput(); });
    } else {
      this.enableInput();
    }
  }

  enableInput() {
    this.inputEnabled = true;
    if (this.guided) this.showHandOnKey(this.round.answers[this.currentPump]);
  }

  // ════════════════════════ KEYPAD INPUT ════════════════════════

  onKey(label) {
    // ─── Pre-flight ───
    // Inputs: pointerup on keypad digit / Clear zones.
    // State mutated: this.input, this.lives, this.currentPump, bar fills, window text.
    // Re-entry guards: this.inputEnabled gates everything; set false the instant a
    //   digit is evaluated (before any async tween), re-enabled only on a wrong
    //   retry. Double-tap / tap-during-anim sees inputEnabled === false and is ignored.
    // Cleanup: keypad destroyed on shutdown; startRound resets state.
    // Manually verified: [x] double-tap [x] tap during fill [x] rapid keys
    if (!this.inputEnabled) return;
    if (label === 'clear') { this.input = ''; this.updateWindow(); return; }
    this.flashKey(label);
    this.input = String(label);
    this.updateWindow();
    this.evaluate();
  }

  evaluate() {
    this.inputEnabled = false;
    this.hideKeyHover();
    const p = this.currentPump;
    if (Number(this.input) === this.round.answers[p]) {
      this.sound.play('correct-choice', { volume: 0.8 });
      this.hideHand();
      this.windowTexts[p].setColor(C_CORRECT);
      // Flow: the pipe connects to the rocket, THEN the bar fills, THEN next pump.
      this.connectPipe(p, () => {
        this.fillSegment(p);
        this.time.delayedCall(1200, () => this.retractPipe(p, () => this.onPumpCorrect()));
      });
    } else {
      this.sound.play('wrong-buzz', { volume: 0.7 });
      this.shakeBarRed(p);
      if (!this.guided) {
        this.lives -= 1;
        this.showHintButton(); // pizza convention: lightbulb pops in on a wrong attempt
      }
      this.time.delayedCall(700, () => {
        this.input = '';
        this.updateWindow();
        if (!this.guided && this.lives <= 0) {
          this.roundFailed();
        } else {
          this.inputEnabled = true;
          if (this.guided) this.showHandOnKey(this.round.answers[p]);
        }
      });
    }
  }

  onPumpCorrect() {
    if (this.currentPump < PUMP_ORDER.length - 1) {
      this.activatePump(this.currentPump + 1);
    } else {
      this.roundSolved();
    }
  }

  // ════════════════════════ SCORING ════════════════════════

  roundSolved() {
    this.inputEnabled = false;
    this.sound.play('correct-answer', { volume: 0.9 });
    this.hideHand();
    this.hideHintButton();

    // Every solved round — the tutorial included (it counts as level 1 of 6) —
    // lights the next green dot on the score panel.
    this.lightDot(gameState.correctCount);
    gameState.correctCount += 1;
    const won = gameState.correctCount >= WIN_DOTS;

    this.slideKeypad(KEYPAD.hiddenY, () => {
      this.keypadUp = false;
      this.showLaunch(won);
    });
  }

  roundFailed() {
    // Lives gone: reveal the correct answers (no dot), then move on.
    this.inputEnabled = false;
    this.hideHand();
    this.hideHintButton();
    PUMP_ORDER.forEach((c, i) => {
      this.windowTexts[i].setText(String(this.round.answers[i])).setColor('#b23b00'); // Orange 6
      this.resetPipe(c);
    });
    this.slideKeypad(KEYPAD.hiddenY, () => { this.keypadUp = false; });
    this.time.delayedCall(1400, () => this.advanceRound());
  }

  lightDot(i) {
    if (i >= this.scoreDots.length) return;
    const dot = this.scoreDots[i];
    dot.setVisible(true).setScale(0);
    this.tweens.add({ targets: dot, scale: 1, duration: 300, ease: 'Back.easeOut' });
  }

  // ════════════════════════ LAUNCH / ADVANCE ════════════════════════

  showLaunch(won) {
    const x = config.width / 2;
    const y = 652;
    this.launchBtn = this.add
      .image(x, y, 'launch-button') // 536:4920
      .setDepth(15)
      .setInteractive({ useHandCursor: true });
    this.launchBtn.once('pointerup', () => this.onLaunch(won));
    // Hover = charcoal texture swap (Cuemath button-state convention), no scaling.
    this.launchBtn.on('pointerover', () => this.launchBtn.setTexture('launch-button-hover'));
    this.launchBtn.on('pointerout', () => this.launchBtn.setTexture('launch-button'));

    this.launchBtn.setScale(0);
    this.tweens.add({ targets: this.launchBtn, scale: 1, duration: 250, ease: 'Back.easeOut' });

    if (this.guided) {
      this.hand.setVisible(true).setPosition(x + 90, y + 16);
      this.handPulse = this.tweens.add({ targets: this.hand, x: x + 110, duration: 600, yoyo: true, repeat: -1 });
    }
  }

  onLaunch(won) {
    if (this.launching) return;
    this.launching = true;
    this.hideHand();
    if (this.launchBtn) {
      this.tweens.killTweensOf(this.launchBtn);
      this.launchBtn.destroy();
      this.launchBtn = null;
    }
    this.launchRocket(() => {
      if (won) this.finishGame(true);
      else this.advanceRound();
    });
  }

  launchRocket(onDone) {
    // The roar starts first; the rocket lifts off 4s into it (engine spool-up),
    // so the sound and the anim peak together. landRocket stops any leftover roar.
    this.launchSfx = this.sound.add('rocket-launch', { volume: 1.5 });
    this.launchSfx.play();
    this.time.delayedCall(4000, () => {
      this.rocket.setVisible(false);
      const anim = this.add.sprite(ROCKET_ANIM.x, ROCKET_ANIM.y, 'rocketAnims').setOrigin(0.5).setDepth(5);
      anim.playReverse('rocketLaunch');
      anim.once('animationcomplete', () => {
        anim.destroy();
        if (onDone) onDone();
      });
    });
  }

  // Same atlas played forward = the native landing (frame 00 is empty sky,
  // frame 59 is the parked pose), ending on the static rocket sprite.
  landRocket(onDone) {
    if (this.launchSfx && this.launchSfx.isPlaying) this.launchSfx.stop();
    const landSfx = this.sound.add('rocket-landing', { volume: 1.5 });
    landSfx.play();
    const anim = this.add
      .sprite(ROCKET_ANIM.x, ROCKET_ANIM.y, 'rocketAnims', 'rocket landing00.png')
      .setOrigin(0.5)
      .setDepth(5);
    anim.play('rocketLaunch');
    anim.once('animationcomplete', () => {
      landSfx.stop(); // landing sound ends with the touchdown, no tail-out
      anim.destroy();
      this.parkRocket();
      if (onDone) onDone();
    });
  }

  retractPipe(p, onDone) {
    const color = PUMP_ORDER[p];
    const pipe = this.pipes[color];
    pipe.playReverse(`pumpAnim-${color}`);
    pipe.once('animationcomplete', () => {
      if (onDone) onDone();
    });
  }

  resetPipe(color) {
    const pipe = this.pipes[color];
    pipe.anims.stop();
    pipe.setFrame(`${PUMP_ANIM_PREFIX[color]}00.png`);
  }

  advanceRound() {
    this.launching = false;
    const next = this.roundIndex + 1;
    // Rounds 1..9 are scored; after round 9 the game ends.
    if (next > 9) {
      this.finishGame(gameState.correctCount >= WIN_DOTS);
      return;
    }
    this.playWarpTransition(() => this.startRound(next));
  }

  playWarpTransition(onDone) {
    // Opaque cover (SPACE_BG) below the video. warp-overlay has a transparent
    // oval, so before the video's first frame decodes the game scene would
    // otherwise flash through it for a frame. The cover makes the transition
    // read as if it starts directly on the animation.
    const cover = this.add
      .rectangle(0, 0, config.width, config.height, 0x191919) // SPACE_BG
      .setOrigin(0)
      .setDepth(99);
    const vid = this.add
      .video(0, 0, 'hyperdriveStart')
      .setOrigin(0)
      .setDepth(100)
      .setMute(true);
    vid.play();
    const overlay = this.add
      .image(0, 0, 'warp-overlay')
      .setOrigin(0)
      .setDepth(101);
    let finished = false;
    const finish = () => {
      if (finished) return;
      finished = true;
      if (cover && cover.scene) cover.destroy();
      if (vid && vid.scene) vid.destroy();
      if (overlay && overlay.scene) overlay.destroy();
      if (onDone) onDone();
      this.cameras.main.fadeIn(300, 0, 0, 0);
    };
    vid.once('complete', finish);
    // Safety: complete may not fire on some browsers — bail after a fixed window.
    this.time.delayedCall(10000, finish);
  }

  finishGame(won) {
    gameState.gameOver = !won;
    this.cameras.main.fadeOut(450, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      // Final launch roar ends the moment the reward/end screen appears.
      if (this.launchSfx && this.launchSfx.isPlaying) this.launchSfx.stop();
      this.scene.start('EndScene');
    });
  }

  // ════════════════════════ PERCENTAGE BAR (Figma 536:7172 — sharp, light) ════════════════════════

  buildBar(round) {
    this.barG.clear();
    this.barFills.forEach(r => r.destroy());
    this.barTexts.forEach(t => t.destroy());
    this.barFills = [];
    this.barTexts = [];
    this.barC.x = 0;

    const left = BAR.cx - BAR.w / 2;
    const top = BAR.cy - BAR.h / 2;
    const innerLeft = left + BAR.border;
    const innerW = BAR.w - 2 * BAR.border;

    // Light segments + sharp-cornered border (no rounding).
    this.barG.fillStyle(C_SEG_EMPTY, 1);
    this.barG.fillRect(left, top, BAR.w, BAR.h);
    this.barG.lineStyle(2, C_DIVIDER, 1);
    this.barG.strokeRect(left, top, BAR.w, BAR.h);

    let segX = innerLeft;
    round.pct.forEach((pct, i) => {
      const color = PUMP_ORDER[i];
      const segW = (pct / 100) * innerW; // math, not traced from Figma pixels

      const fill = this.add
        .rectangle(segX, BAR.cy, segW, BAR.h - 2 * BAR.border, PUMP_COLOR[color])
        .setOrigin(0, 0.5); // left-anchored so it grows rightward when filled
      fill.scaleX = 0; // empty until answered
      this.barC.add(fill);
      this.barFills.push(fill);

      const txt = this.add
        .text(segX + segW / 2, BAR.cy, `${pct}%`, {
          fontFamily: 'Athletics-Bold', fontSize: '19px', color: PUMP_COLOR_CSS[color],
        })
        .setOrigin(0.5);
      this.barC.add(txt);
      this.barTexts.push(txt);

      if (i < round.pct.length - 1) {
        this.barG.lineStyle(2, C_DIVIDER, 1);
        this.barG.lineBetween(segX + segW, top + 2, segX + segW, top + BAR.h - 2);
      }
      segX += segW;
    });
  }

  fillSegment(i) {
    // Grow the colour from the left edge — a "filling up" wipe, not a fade.
    this.sound.play('liquid-whoosh', { volume: 0.8 });
    this.tweens.add({
      targets: this.barFills[i],
      scaleX: 1,
      duration: 420,
      ease: 'Sine.easeOut',
      onComplete: () => {
        // Last segment = the bar just hit 100%.
        if (i === PUMP_ORDER.length - 1) this.sound.play('bar-completed', { volume: 0.8 });
      },
    });
    this.barTexts[i].setColor('#ffffff');
  }

  shakeBarRed(i) {
    const fill = this.barFills[i];
    fill.setFillStyle(C_WRONG);
    fill.scaleX = 1; // flash the red across the whole segment
    this.tweens.add({
      targets: this.barC, x: { from: -8, to: 8 }, duration: 60, yoyo: true, repeat: 4,
      onComplete: () => {
        this.barC.x = 0;
        fill.setFillStyle(PUMP_COLOR[PUMP_ORDER[i]]);
        fill.scaleX = 0; // empty again for the retry
      },
    });
  }

  // ════════════════════════ PIPES ════════════════════════

  connectPipe(p, onDone) {
    const color = PUMP_ORDER[p];
    const pipe = this.pipes[color].setVisible(true).setAlpha(1);
    pipe.play(`pumpAnim-${color}`);
    pipe.once('animationcomplete', () => {
      if (onDone) onDone();
    });
  }

  // ════════════════════════ KEYPAD ════════════════════════

  buildKeypad() {
    this.keypad = this.add.container(KEYPAD.x, KEYPAD.hiddenY).setDepth(10);
    this.keypad.add(this.add.image(0, 0, 'keypad'));
    this.keyFlash = this.add.rectangle(0, 0, KEY_W, KEY_H, 0x333333, 0).setOrigin(0.5);
    this.keypad.add(this.keyFlash);
    this.buildKeyHover();
    KEY_LAYOUT.forEach(({ label, dx, dy, wide }) => {
      const zone = this.add.zone(dx, dy, wide ? KEY_WIDE_W : KEY_W, KEY_H).setOrigin(0.5)
        .setInteractive({ useHandCursor: true });
      zone.on('pointerover', () => { if (this.inputEnabled) this.showKeyHover({ label, dx, dy, wide }); });
      zone.on('pointerout', () => this.hideKeyHover());
      zone.on('pointerup', () => this.onKey(label));
      this.keypad.add(zone);
    });
  }

  // Per-key hover highlight (Figma 536:40585): the key face turns charcoal
  // #2b2b2b with a 75%-opacity shadow offset 3.6 below and a white
  // Athletics-Black digit — drawn at exactly the white key's 48×57.6
  // footprint (radius 9.6) so the swap sits pixel-on-pixel over the
  // resting key in keypad.png.
  buildKeyHover() {
    this.keyHoverG = this.add.graphics();
    this.keyHoverText = this.add
      .text(0, 0, '', { fontFamily: 'Athletics-Black', color: '#ffffff' })
      .setOrigin(0.5);
    this.keyHover = this.add.container(0, 0, [this.keyHoverG, this.keyHoverText]).setVisible(false);
    this.keypad.add(this.keyHover);
  }

  showKeyHover(key) {
    const w = key.wide ? KEY_WIDE_W : KEY_W;
    const left = key.dx - w / 2;
    const top = key.dy - KEY_H / 2;
    const r = 9.6;
    const g = this.keyHoverG;
    g.clear();
    g.fillStyle(0x2b2b2b, 0.75); // shadow, offset 3.6 down
    g.fillRoundedRect(left, top + 3.6, w, KEY_H, r);
    g.fillStyle(0x2b2b2b, 1); // charcoal key face (design border = fill colour)
    g.fillRoundedRect(left, top, w, KEY_H, r);
    this.keyHoverText
      .setText(key.label === 'clear' ? 'Clear' : String(key.label))
      .setFontSize(key.wide ? 22 : 28.8)
      .setPosition(key.dx, key.dy);
    this.keyHover.setVisible(true);
  }

  hideKeyHover() {
    if (this.keyHover) this.keyHover.setVisible(false);
  }

  slideKeypad(toY, onDone) {
    this.hideKeyHover();
    this.tweens.add({ targets: this.keypad, y: toY, duration: 450, ease: 'Cubic.easeOut', onComplete: onDone });
  }

  flashKey(label) {
    const key = KEY_LAYOUT.find(k => k.label === label);
    if (!key) return;
    this.keyFlash.setPosition(key.dx, key.dy).setSize(key.wide ? KEY_WIDE_W : KEY_W, KEY_H);
    this.keyFlash.setFillStyle(0x333333, 0.18);
    this.tweens.add({ targets: this.keyFlash, fillAlpha: 0, duration: 220 });
  }

  // ════════════════════════ TUTORIAL HAND ════════════════════════

  showHandOnKey(digit) {
    const key = KEY_LAYOUT.find(k => k.label === digit);
    if (!key) return;
    this.hand.setVisible(true).setPosition(this.keypad.x + key.dx + 6, this.keypad.y + key.dy + 6);
    if (this.handPulse) this.handPulse.stop();
    this.handPulse = this.tweens.add({ targets: this.hand, scale: { from: 1, to: 0.88 }, duration: 520, yoyo: true, repeat: -1 });
  }

  hideHand() {
    if (this.handPulse) { this.handPulse.stop(); this.handPulse = null; }
    this.hand.setVisible(false).setScale(1);
  }

  // ════════════════════════ HINT (pizza-game convention) ════════════════════════

  buildHintButton() {
    // Depth sits ABOVE the modal stack (overlay 50 / card 51 / content 52) so
    // the lightbulb badge peeks out over the card, matching the pizza-game
    // composition (its D.buttons 35 > D.hintModal 32).
    const btn = this.add
      .image(HINT.x, HINT.y, 'hint-button')
      .setDisplaySize(HINT.size, HINT.size)
      .setDepth(53)
      .setInteractive({ useHandCursor: true })
      .setVisible(false);
    btn.on('pointerup', () => this.showHint());
    return btn;
  }

  showHintButton() {
    this.hintBtn.setVisible(true);
    // Tween displayWidth/Height (size-based), not `scale` — the PNG is a 2x
    // export and setDisplaySize already baked the right scale.
    this.hintBtn.setDisplaySize(HINT.size * 0.8, HINT.size * 0.8);
    this.tweens.add({
      targets: this.hintBtn,
      displayWidth: HINT.size,
      displayHeight: HINT.size,
      duration: 220,
      ease: 'Back.easeOut',
    });
  }

  hideHintButton() {
    this.hintBtn.setVisible(false);
    if (this.hintModal) this.hintModal.dismiss();
  }

  showHint() {
    // ─── Pre-flight ───
    // Inputs: pointerup on hint button; overlay / cross close the modal.
    // State mutated: this.hintModal only (created here, nulled by dismiss).
    // Re-entry guards: early return while open; the full-screen interactive
    //   overlay swallows pointer events so the keypad can't fire underneath.
    // Cleanup: dismiss destroys all four objects; hideHintButton (round end,
    //   round start) dismisses too if the modal is still open.
    // Rollback: overlay or cross click closes; keypad state is untouched.
    // Manually verified: [x] double click button [x] click during round-end timer [x] rapid clicks
    if (this.hintModal) return; // already open
    const overlay = this.add
      .rectangle(config.width / 2, config.height / 2, config.width, config.height, 0x000000, 0.5)
      .setInteractive()
      .setDepth(50);

    const card = this.add.graphics().setDepth(51);
    card.fillStyle(0xffffff, 1);
    card.fillRoundedRect(HINT_CARD.x, HINT_CARD.y, HINT_CARD.w, HINT_CARD.h, HINT_CARD.r);

    const [a, b, c] = this.round.pct;
    const g = gcd(gcd(a, b), c);
    const body = this.add
      .text(
        HINT_CARD.x + HINT_CARD.w / 2,
        HINT_CARD.y + HINT_CARD.h / 2,
        `Divide each percent by ${g} — the biggest number that divides ${a}, ${b} and ${c}.`,
        {
          fontFamily: 'Athletics-Regular',
          fontSize: '28px',
          color: '#2b2b2b',
          align: 'center',
          wordWrap: { width: HINT_CARD.w - 80 },
        },
      )
      .setOrigin(0.5)
      .setDepth(52);

    const close = this.add
      .image(HINT_CROSS.cx, HINT_CROSS.cy, 'hint-close')
      .setDisplaySize(HINT_CROSS.s, HINT_CROSS.s)
      .setInteractive({ useHandCursor: true })
      .setDepth(52);

    const dismiss = () => {
      overlay.destroy();
      card.destroy();
      body.destroy();
      close.destroy();
      this.hintModal = null;
    };
    close.on('pointerup', dismiss);
    overlay.on('pointerup', dismiss);
    this.hintModal = { overlay, card, body, close, dismiss };
  }

  // ════════════════════════ helpers ════════════════════════

  updateWindow() {
    this.windowTexts[this.currentPump].setText(this.input);
  }

  pulsePump(p) {
    this.tweens.add({ targets: this.pumps[p], scale: { from: 1, to: 1.05 }, duration: 220, yoyo: true });
  }
}
